import { type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import {
	DEFAULT_LANG,
	type LanguageCode,
	SUPPORTED_LANGS,
} from "@/i18n/constants";

const SUPPORTED = SUPPORTED_LANGS as readonly string[];

const LIKELY_LANG_CODE = /^[a-z]{2}$/i;

const getFirstSegment = (pathname: string): string => {
	const match = pathname.match(/^\/([^/]+)/);
	return match?.[1] ?? "";
};

const LocaleGate = ({ children }: { children: ReactNode }) => {
	const { i18n } = useTranslation();
	const location = useLocation();
	const firstSeg = getFirstSegment(location.pathname);

	const restAfterFirstSeg = (() => {
		const trimmed = location.pathname.replace(/^\/[^/]+/, "");
		return trimmed || "/";
	})();

	const isCanonicalEnAlias = firstSeg === DEFAULT_LANG;
	const isUnsupportedLangCode =
		LIKELY_LANG_CODE.test(firstSeg) && !SUPPORTED.includes(firstSeg);

	const urlLang: LanguageCode | null =
		firstSeg && SUPPORTED.includes(firstSeg) && firstSeg !== DEFAULT_LANG
			? (firstSeg as LanguageCode)
			: null;
	const targetLang: LanguageCode = urlLang ?? DEFAULT_LANG;

	useEffect(() => {
		if (i18n.language !== targetLang) {
			void i18n.changeLanguage(targetLang);
		}
	}, [i18n, targetLang]);

	if (isCanonicalEnAlias || isUnsupportedLangCode) {
		return (
			<Navigate
				to={`${restAfterFirstSeg}${location.search}${location.hash}`}
				replace
			/>
		);
	}

	return <>{children}</>;
};

export { LocaleGate };
